import { BookA, ChevronLeft, ChevronRight, MessageSquareText, Target } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { BackLabel } from "@/book/components/back";
import { ExperienceList } from "@/book/components/experience-list";
import { ExerciseRow } from "@/book/components/rows";
import { useChapter, type ChapterCounts } from "@/shared/api/book";
import type { ReactNode } from "react";
import { Markdown } from "@/shared/ui/markdown";
import { SubHeader } from "@/shared/ui/page-header";
import { SectionLabel } from "@/shared/ui/section-label";
import { Empty, ErrorNote, ListSkeleton, StatusChip } from "@/shared/ui/state";
import { Tabs } from "@/shared/ui/tabs";

type TabKey = "exercises" | "glossary" | "experiences";

/** Az első fül, amelyben van tartalom — így üres fülre nem esik be az olvasó. */
function firstFilledTab(counts: ChapterCounts): TabKey {
  if (counts.exercises > 0) return "exercises";
  if (counts.glossary > 0) return "glossary";
  if (counts.experiences > 0) return "experiences";
  return "exercises";
}

/** Fejezet-oldal: egy helyen minden, ami a fejezethez tartozik (rendszerterv 8.).
 *
 *  A három fő tartalom (gyakorlatok · fogalmak · tapasztalatok) a cím alatti lapfüleken van,
 *  hogy ne kelljen végiggörgetni a fejezetet; az olvasnivalók és a javítások a fülek alatt
 *  maradnak, szekcióként. */
export function ChapterPage() {
  const { chapterId } = useParams();
  const id = Number(chapterId);
  const { data, isPending, isError } = useChapter(id);
  const [tab, setTab] = useState<TabKey | null>(null);

  if (isPending) return <ListSkeleton rows={6} />;
  if (isError || !data) return <ErrorNote>Ez a fejezet nem érhető el.</ErrorNote>;

  const { chapter, exercises, glossary, errata } = data;
  const counts = chapter.counts;
  const empty = counts.exercises + counts.glossary + counts.experiences + counts.errata === 0;
  const active = tab ?? firstFilledTab(counts);
  const number = chapter.number.replace(/^0/, "");

  return (
    // a fejezetről megnyitott gyakorlat fejléce ide kínál visszautat: „‹ 3. fejezet"
    <BackLabel value={`${number}. fejezet`}>
      <div className="flex flex-col gap-6">
        <SubHeader to="/app/book#fejezetek" parent="Fejezetek" />

        <div className="flex flex-col gap-2">
          {/* A sorszám szemcímkeként ül a cím fölött (design-rendszer 3., „Szekció-címke") —
              így egy főcím van az oldalon, és a szám nem verseng vele. */}
          <span className="section-label text-gold-text">{number}. fejezet</span>
          <h1 className="text-[28px] font-extrabold leading-[1.1] tracking-[-0.01em]">
            {chapter.title}
          </h1>
          {chapter.lead && <Markdown>{chapter.lead}</Markdown>}
        </div>

        {empty && <Empty>Ehhez a fejezethez még nincs feltöltve tartalom.</Empty>}

        {!empty && (
          <div className="-mt-2 flex flex-col gap-4">
            <Tabs
              label="A fejezet tartalma"
              value={active}
              onChange={setTab}
              tabs={[
                // ugyanazok az ikonok, mint a kezdőlap csempéin — ugyanaz a tartalom
                { value: "exercises", label: "Gyakorlatok", icon: Target },
                { value: "glossary", label: "Fogalmak", icon: BookA },
                { value: "experiences", label: "Tapasztalatok", icon: MessageSquareText },
              ]}
            />

            {active === "exercises" &&
              (exercises.length === 0 ? (
                <Empty>Ehhez a fejezethez még nincs gyakorlat.</Empty>
              ) : (
                <TabPanel
                  count={counts.exercises}
                  shown={exercises.length}
                  more={`/app/book/gyakorlatok?chapter_id=${chapter.id}`}
                >
                  {exercises.map((e) => (
                    <ExerciseRow key={e.id} exercise={e} hideChapter />
                  ))}
                </TabPanel>
              ))}

            {active === "glossary" &&
              (glossary.length === 0 ? (
                <Empty>Ehhez a fejezethez még nincs fogalom.</Empty>
              ) : (
                <TabPanel
                  count={counts.glossary}
                  shown={glossary.length}
                  more={`/app/book/fogalomtar?chapter_id=${chapter.id}`}
                >
                  {glossary.map((g) => (
                    <div key={g.id} className="flex flex-col gap-1 border-t border-line py-3">
                      <span className="text-[15px] font-semibold">
                        {g.term_en ?? g.terms_hu}
                        {g.term_en && (
                          <span className="font-normal text-ink-soft"> · {g.terms_hu}</span>
                        )}
                      </span>
                      {g.body && <Markdown className="text-[14px]">{g.body}</Markdown>}
                    </div>
                  ))}
                </TabPanel>
              ))}

            {/* Teljes lista, helyben lapozva — és látszik, melyik gyakorlathoz tartozik. */}
            {active === "experiences" && (
              <ExperienceList
                chapterId={chapter.id}
                label={null}
                emptyText="Ehhez a fejezethez még nincs megosztott tapasztalat."
              />
            )}
          </div>
        )}

        <Section
          label="Amit ma másképp látunk"
          count={chapter.counts.errata}
          more={`/app/book/hibajegyzek?chapter_id=${chapter.id}`}
          shown={errata.length}
        >
          {errata.map((e) => (
            <div key={e.id} className="flex flex-col gap-1 border-t border-line py-3">
              {e.locator && <span className="text-[12px] font-semibold text-muted">{e.locator}</span>}
              <Markdown className="text-[14px]">{e.body}</Markdown>
              <StatusChip status={e.status} />
            </div>
          ))}
        </Section>

        <nav className="flex items-center justify-between gap-3 border-t border-line pt-4">
          {data.prev ? (
            <Link
              to={`/app/book/fejezetek/${data.prev.id}`}
              className="flex min-h-11 items-center gap-1 text-[13px] font-semibold text-gold-text"
            >
              <ChevronLeft size={16} />
              {data.prev.number}. {data.prev.title}
            </Link>
          ) : (
            <span />
          )}
          {data.next && (
            <Link
              to={`/app/book/fejezetek/${data.next.id}`}
              className="flex min-h-11 items-center gap-1 text-right text-[13px] font-semibold text-gold-text"
            >
              {data.next.number}. {data.next.title}
              <ChevronRight size={16} />
            </Link>
          )}
        </nav>
      </div>
    </BackLabel>
  );
}

/** Fül-tartalom: a lista, alatta „Mind (n)" link, ha a fejezet-oldalon nem fér ki minden. */
function TabPanel({
  count,
  more,
  shown,
  children,
}: {
  count: number;
  more: string;
  shown: number;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col">
      {children}
      {count > shown && (
        <Link
          to={more}
          className="flex min-h-11 items-center border-t border-line text-[13px] font-semibold text-gold-text"
        >
          Mind ({count})
        </Link>
      )}
    </div>
  );
}

function Section({
  label,
  count,
  more,
  shown,
  children,
}: {
  label: string;
  count: number;
  more?: string;
  shown: number;
  children: ReactNode;
}) {
  if (shown === 0) return null;
  return (
    <section className="flex flex-col">
      <div className="mb-2 flex items-center justify-between">
        <SectionLabel>{label}</SectionLabel>
        {more && count > shown && (
          <Link to={more} className="text-[13px] font-semibold text-gold-text">
            Mind ({count})
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}
